import { useState, useCallback } from 'react';
import { apiFetch } from '../../../auth/apiFetch';

const apiBaseUrl = process.env.REACT_APP_API_URL || '';

/**
 * Hook for managing chunk_data version history.
 * Fetches available versions and allows selecting/loading a specific version.
 */
export function useChunkVersions(ean) {
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [error, setError] = useState(null);

  const hasVersions = versions.length > 0;

  /**
   * Fetch available chunk_data versions for the given EAN.
   */
  const fetchVersions = useCallback(async () => {
    if (!ean) {
      setVersions([]);
      return;
    }

    setIsLoadingVersions(true);
    setError(null);

    try {
      const response = await apiFetch(`${apiBaseUrl}/api/chunk-data/ean/${ean}/versions`);

      if (!response.ok) {
        if (response.status === 404) {
          // Product not found or no versions - this is fine, just means no history
          setVersions([]);
          return;
        }
        throw new Error(`Failed to fetch versions: ${response.status}`);
      }

      const data = await response.json();
      setVersions(data.versions || []);
    } catch (err) {
      console.error('Error fetching chunk versions:', err);
      setError(err.message);
      setVersions([]);
    } finally {
      setIsLoadingVersions(false);
    }
  }, [ean]);

  /**
   * Select and load a specific version by ID.
   */
  const selectVersion = useCallback(async (versionId) => {
    if (!versionId) {
      setSelectedVersion(null);
      return;
    }

    setIsLoadingDetail(true);
    setError(null);

    try {
      const response = await apiFetch(`${apiBaseUrl}/api/chunk-data/versions/${versionId}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch version: ${response.status}`);
      }

      const data = await response.json();
      setSelectedVersion(data);
    } catch (err) {
      console.error('Error fetching chunk version detail:', err);
      setError(err.message);
      setSelectedVersion(null);
    } finally {
      setIsLoadingDetail(false);
    }
  }, []);

  /**
   * Clear the selected version.
   */
  const clearSelection = useCallback(() => {
    setSelectedVersion(null);
  }, []);

  return {
    versions,
    selectedVersion,
    isLoadingVersions,
    isLoadingDetail,
    isLoading: isLoadingVersions || isLoadingDetail,
    error,
    hasVersions,
    fetchVersions,
    selectVersion,
    clearSelection,
  };
}
